import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Popover,
  PopoverActions,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { runMultiplexNormalization } from 'api/platform';
import { SlideChannelNormalizationResultSummary } from 'api/slideMultiplexChannelNormalizations';
import { modelTypeNormalization } from 'components/Pages/Jobs/inferenceFieldsOptions';
import { PanelSlideSummary } from 'components/StudyDashboard/MultiplexPanels/PanelSlideSummary';
import { NormalizationMethod } from 'interfaces/jobs/multiplex/normalizationParams';
import { MultiplexPanelView } from 'interfaces/multiplexPanels';
import { Permission } from 'interfaces/permissionOption';
import { MULTIPLEX_STAIN_ID, StainType } from 'interfaces/stainType';
import { every, filter, find, includes, intersection, join, map, min, some, uniq } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { getPanelChannelModalityLabels } from 'utils/multiplex/panelChannelPresentation';
import { useModelTypeOptions } from 'utils/queryHooks/useModelTypeOptions';
import { CasesParams } from 'utils/useCasesParams/types';
import { usePermissions } from 'utils/usePermissions';
import { defaultHistogramConfig } from '../Multiplex/RunHistogram/HistogramParams';
import {
  defaultChannelNormalizationConfig,
  defaultNormalizationParams,
  normalizationSchema,
} from '../Multiplex/RunNormalization/defaults';
import NormalizationParamsForm from '../Multiplex/RunNormalization/NormalizationParamsForm';
import { hasCompleteMarkerNormalization } from './bulkMultiplexNormalizationForm';

interface Props {
  disabled?: boolean;
  panel: MultiplexPanelView;
  casesParams: CasesParams;
  markerIds: string[];
  selectedMarkerIds: string[];
  onClearSelection: () => void;
  summaries: SlideChannelNormalizationResultSummary[];
  stains: StainType[];
  getMarkerLabel: (markerId: string) => string;
  maximumByMarker: Record<string, number | undefined>;
  pendingMarkerIds: string[];
  onStageRanges: (markerIds: string[], minimum: number, maximum: number) => void;
}

type ActionKind = 'normalization' | 'rna' | 'bulk';
type MarkerScope = 'missing' | 'all' | 'selected';
interface NormalizationSettings {
  scope: MarkerScope;
  method: NormalizationMethod;
  modelOverride: string | null;
  parameters: typeof defaultChannelNormalizationConfig;
  slideScope: 'all' | 'selected';
  selectedSlideIds: string[];
}

export const PanelNormalizationActions = ({
  disabled = false,
  panel,
  casesParams,
  markerIds,
  selectedMarkerIds,
  onClearSelection,
  summaries,
  stains,
  getMarkerLabel,
  maximumByMarker,
  pendingMarkerIds,
  onStageRanges,
}: Props) => {
  const { hasPermission } = usePermissions();
  const canRun = hasPermission(Permission.ExecuteMultiplexNormalization);
  const studyId = casesParams.studyId || casesParams.filters?.studyId;
  const modelTypes = useModelTypeOptions({ enabled: canRun });
  const defaultModelUrl = find(
    modelTypes.data || [],
    (modelType) =>
      modelType.type === modelTypeNormalization.apiModelValue && modelType.stainingMethod === MULTIPLEX_STAIN_ID
  )?.defaultModelUrl;
  const queryClient = useQueryClient();
  const submissionLock = useRef(false);
  const popoverActions = useRef<PopoverActions | null>(null);
  const failureAlert = useRef<HTMLDivElement | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [kind, setKind] = useState<ActionKind>('normalization');
  const [scope, setScope] = useState<MarkerScope>('missing');
  const [method, setMethod] = useState(NormalizationMethod.Auto);
  const [modelOverride, setModelOverride] = useState<string | null>(null);
  const [parameters, setParameters] = useState(defaultChannelNormalizationConfig);
  const [slideScope, setSlideScope] = useState<'all' | 'selected'>('all');
  const [selectedSlideIds, setSelectedSlideIds] = useState<string[]>([]);
  const [minimum, setMinimum] = useState('0');
  const [maximum, setMaximum] = useState('');
  const [acknowledgedScope, setAcknowledgedScope] = useState<string | null>(null);
  const [confirmedSettings, setConfirmedSettings] = useState<NormalizationSettings>({
    scope: 'missing',
    method: NormalizationMethod.Auto,
    modelOverride: null,
    parameters: defaultChannelNormalizationConfig,
    slideScope: 'all',
    selectedSlideIds: [],
  });
  const mutation = useMutation({
    mutationFn: async (params: Parameters<typeof runMultiplexNormalization>[0]) => {
      const response = await runMultiplexNormalization(params);
      if (response === null || response === undefined) throw new Error('No normalization job was returned.');
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['bulkMultiplexNormalizationSummary']);
    },
  });
  useEffect(() => {
    if (mutation.isError && anchor) failureAlert.current?.scrollIntoView({ block: 'nearest' });
  }, [mutation.isError, anchor]);
  const memberSlideIds = map(panel.slides, 'slideId');
  const selected = intersection(selectedMarkerIds, markerIds);
  const hasStaleMarkerSelection = selected.length !== selectedMarkerIds.length;
  const modalities = (markerId: string) => getPanelChannelModalityLabels(panel, markerId, stains);
  const mifMarkerIds = filter(markerIds, (markerId) => {
    const labels = modalities(markerId);
    return labels.length > 0 && every(labels, (label) => label === 'mIF');
  });
  const rnaMarkerIds = filter(markerIds, (markerId) => includes(modalities(markerId), 'RNA'));
  const missingMifMarkerIds = filter(
    mifMarkerIds,
    (markerId) => !hasCompleteMarkerNormalization(summaries, markerId, memberSlideIds)
  );
  const primaryMarkerIds = selected.length ? selected : missingMifMarkerIds;
  const pendingPrimaryMarkerCount = intersection(primaryMarkerIds, pendingMarkerIds).length;
  const primarySlideIds =
    selected.length && confirmedSettings.slideScope === 'selected'
      ? intersection(confirmedSettings.selectedSlideIds, memberSlideIds)
      : memberSlideIds;
  const targetMarkerIds =
    kind === 'rna'
      ? scope === 'selected'
        ? intersection(selected, rnaMarkerIds)
        : rnaMarkerIds
      : kind === 'bulk'
      ? selected
      : scope === 'selected'
      ? selected
      : scope === 'all'
      ? mifMarkerIds
      : missingMifMarkerIds;
  const targetSlideIds = slideScope === 'all' ? memberSlideIds : intersection(selectedSlideIds, memberSlideIds);
  const selectedModelUrl = modelOverride ?? defaultModelUrl;
  const warnings = (markers: string[], slides: string[], currentMethod: NormalizationMethod) => {
    if (currentMethod !== NormalizationMethod.Auto) return [];
    const messages: string[] = [];
    const rna = filter(markers, (markerId) => includes(modalities(markerId), 'RNA'));
    if (rna.length)
      messages.push(
        `Automatic normalization is not optimized for RNA channels: ${join(
          map(rna, getMarkerLabel),
          ', '
        )}. Bulk visualization ranges are recommended.`
      );
    const repeated = filter(markers, (markerId) =>
      some(
        filter(summaries, (summary) => includes(slides, summary.slideId)),
        (summary) =>
          some(
            Object.values(summary.normalizationsSummary),
            (channel) => channel.markerType === markerId && channel.wasAutoNormalized === true
          )
      )
    );
    if (repeated.length)
      messages.push(
        `Already auto-normalized on chosen slides: ${join(
          map(repeated, getMarkerLabel),
          ', '
        )}. Running again creates a new normalization result.`
      );
    return messages;
  };
  const warningMessages = kind === 'normalization' ? warnings(targetMarkerIds, targetSlideIds, method) : [];
  const scopeSignature = JSON.stringify({
    markerIds: targetMarkerIds,
    slideIds: targetSlideIds,
    method,
    modelUrl: selectedModelUrl,
  });
  const maximumValue = every(targetMarkerIds, (markerId) => Number.isFinite(maximumByMarker[markerId]))
    ? min(map(targetMarkerIds, (markerId) => maximumByMarker[markerId]!))
    : undefined;
  const rangesValid =
    minimum !== '' &&
    maximum !== '' &&
    Number.isInteger(Number(minimum)) &&
    Number.isInteger(Number(maximum)) &&
    Number(minimum) >= 0 &&
    Number(minimum) < Number(maximum) &&
    maximumValue !== undefined &&
    Number(maximum) <= maximumValue;
  const rangesHaveFractionalValues = some(
    [minimum, maximum],
    (value) => value !== '' && Number.isFinite(Number(value)) && !Number.isInteger(Number(value))
  );
  const hasAllMarkers = (markers: string[], slides: string[]) =>
    every(slides, (slideId) => {
      const slide = find(panel.slides, { slideId });
      return slide && every(markers, (markerId) => includes(Object.values(slide.channelMarkerTypes), markerId));
    });
  const hasKnownAutomaticHistory = (markers: string[], slides: string[]) =>
    every(slides, (slideId) => {
      const summary = find(summaries, { slideId });
      if (!summary) return true;
      return every(markers, (markerId) => {
        const channels = filter(
          Object.values(summary.normalizationsSummary),
          (channel) => channel.markerType === markerId
        );
        return channels.length > 0 && every(channels, (channel) => typeof channel.wasAutoNormalized === 'boolean');
      });
    });
  const runValid =
    Boolean(studyId) &&
    !hasStaleMarkerSelection &&
    !disabled &&
    canRun &&
    targetMarkerIds.length > 0 &&
    targetSlideIds.length > 0 &&
    hasAllMarkers(targetMarkerIds, targetSlideIds) &&
    (method !== NormalizationMethod.Auto || hasKnownAutomaticHistory(targetMarkerIds, targetSlideIds)) &&
    !some(targetMarkerIds, (markerId) => includes(pendingMarkerIds, markerId)) &&
    (method === NormalizationMethod.Auto ? Boolean(selectedModelUrl) : normalizationSchema.isValidSync(parameters)) &&
    (!warningMessages.length || acknowledgedScope === scopeSignature) &&
    !mutation.isLoading;
  const primaryValid =
    Boolean(studyId) &&
    !hasStaleMarkerSelection &&
    !disabled &&
    canRun &&
    primaryMarkerIds.length > 0 &&
    primarySlideIds.length > 0 &&
    Boolean(defaultModelUrl) &&
    hasAllMarkers(primaryMarkerIds, primarySlideIds) &&
    hasKnownAutomaticHistory(primaryMarkerIds, primarySlideIds) &&
    !some(primaryMarkerIds, (markerId) => includes(pendingMarkerIds, markerId)) &&
    !mutation.isLoading;

  const close = () => {
    if (!disabled && !mutation.isLoading) setAnchor(null);
  };
  const openAction = (event: React.MouseEvent<HTMLElement>, action: ActionKind, defaultRun = false) => {
    setKind(action);
    setScope(
      action === 'bulk'
        ? 'selected'
        : action === 'rna'
        ? intersection(selected, rnaMarkerIds).length
          ? 'selected'
          : 'all'
        : selected.length
        ? 'selected'
        : confirmedSettings.scope === 'all' && !defaultRun
        ? 'all'
        : 'missing'
    );
    if (action === 'normalization') {
      setMethod(defaultRun ? NormalizationMethod.Auto : confirmedSettings.method);
      setModelOverride(defaultRun ? null : confirmedSettings.modelOverride);
      setParameters(confirmedSettings.parameters);
      setSlideScope(confirmedSettings.slideScope);
      setSelectedSlideIds(confirmedSettings.selectedSlideIds);
    }
    setMinimum('0');
    const rangeTargets =
      action === 'rna'
        ? intersection(selected, rnaMarkerIds).length
          ? intersection(selected, rnaMarkerIds)
          : rnaMarkerIds
        : selected;
    setMaximum(
      every(rangeTargets, (markerId) => Number.isFinite(maximumByMarker[markerId]))
        ? String(min(map(rangeTargets, (markerId) => maximumByMarker[markerId]!)) ?? '')
        : ''
    );
    setAcknowledgedScope(null);
    mutation.reset();
    setAnchor(event.currentTarget);
  };
  const run = async (markers: string[], slides: string[], currentMethod: NormalizationMethod, modelUrl?: string) => {
    if (submissionLock.current || !studyId || !markers.length || !slides.length || !hasAllMarkers(markers, slides))
      return false;
    submissionLock.current = true;
    try {
      await mutation.mutateAsync({
        ...casesParams,
        studyId,
        slidesMode: true,
        slideIdsToInclude: slides,
        slideIdsToExclude: undefined,
        caseIdsToInclude: undefined,
        caseIdsToExclude: undefined,
        jobName: `${panel.name || panel.panelId.replace('panel-', 'Panel ')} normalization`,
        jobDescription: '',
        configParams: {
          histogramNormalization: {
            ...defaultNormalizationParams,
            method: currentMethod,
            channels: markers,
            modelUrl: currentMethod === NormalizationMethod.Auto ? modelUrl : undefined,
            baseParams: currentMethod === NormalizationMethod.Parametrized ? parameters : {},
            paramOverrideChannelGroups: [],
          },
          histogramCreation: defaultHistogramConfig,
        },
      });
      setAnchor(null);
      return true;
    } catch {
      return false;
    } finally {
      submissionLock.current = false;
    }
  };
  const primaryClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!primaryValid) return;
    if (warnings(primaryMarkerIds, primarySlideIds, NormalizationMethod.Auto).length)
      openAction(event, 'normalization', true);
    else void run(primaryMarkerIds, primarySlideIds, NormalizationMethod.Auto, defaultModelUrl);
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        {canRun && (
          <Button
            variant="contained"
            color="primary"
            startIcon={
              mutation.isLoading && !anchor ? (
                <CircularProgress size={18} color="inherit" aria-label="Starting normalization" />
              ) : (
                <PlayArrowOutlinedIcon />
              )
            }
            aria-busy={mutation.isLoading}
            disabled={!primaryValid}
            onClick={primaryClick}
          >
            {mutation.isLoading && !anchor
              ? 'Starting normalization...'
              : `Auto-normalize ${primaryMarkerIds.length} ${
                  selected.length ? `selected ${primaryMarkerIds.length === 1 ? 'channel' : 'channels'}` : 'missing mIF'
                }`}
          </Button>
        )}
        <Button
          variant="outlined"
          color="secondary"
          disabled={disabled || !rnaMarkerIds.length || mutation.isLoading}
          onClick={(event) => openAction(event, 'rna')}
        >
          {intersection(selected, rnaMarkerIds).length
            ? `Set ranges for ${intersection(selected, rnaMarkerIds).length} selected RNA`
            : 'Set RNA ranges'}
        </Button>
        {canRun && (
          <Button
            color="inherit"
            endIcon={<ExpandMoreOutlinedIcon />}
            disabled={disabled || mutation.isLoading}
            onClick={(event) => openAction(event, 'normalization')}
          >
            More
          </Button>
        )}
      </Stack>
      {canRun && pendingPrimaryMarkerCount > 0 && (
        <Typography variant="caption" color="text.secondary">
          Save or discard {pendingPrimaryMarkerCount} edited {pendingPrimaryMarkerCount === 1 ? 'range' : 'ranges'}{' '}
          before normalization.
        </Typography>
      )}
      {canRun && !studyId && (
        <Typography variant="caption" color="text.secondary">
          Select a study before running normalization.
        </Typography>
      )}
      {selectedMarkerIds.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {selected.length} selected
          </Typography>
          {selected.length > 0 && !every(selected, (markerId) => includes(rnaMarkerIds, markerId)) && (
            <Button
              color="inherit"
              disabled={disabled || mutation.isLoading || hasStaleMarkerSelection}
              onClick={(event) => openAction(event, 'bulk')}
            >
              Edit selected ranges
            </Button>
          )}
          <Button
            color="inherit"
            startIcon={<ClearOutlinedIcon />}
            disabled={disabled || mutation.isLoading}
            onClick={onClearSelection}
          >
            Clear selection
          </Button>
        </Stack>
      )}
      {hasStaleMarkerSelection && (
        <Alert severity="info">Selected channels changed. Clear selection and select again.</Alert>
      )}
      {canRun && !defaultModelUrl && (
        <Typography variant="caption" color="text.secondary">
          {modelTypes.isLoading
            ? 'Loading default normalization model…'
            : 'Default normalization model unavailable. Choose a model in More.'}
        </Typography>
      )}
      {canRun && primaryMarkerIds.length > 0 && !hasKnownAutomaticHistory(primaryMarkerIds, primarySlideIds) && (
        <Typography variant="caption" color="text.secondary">
          Automatic-normalization history is unavailable. Reload panel information before running.
        </Typography>
      )}
      {mutation.isError && !anchor && (
        <Alert severity="error">
          Normalization could not be started. Your selection and settings are retained; check jobs before retrying.
        </Alert>
      )}
      {mutation.isSuccess && (
        <Alert severity="success">
          Normalization job started for this panel. Current ranges update when results become available.
        </Alert>
      )}
      <Popover
        action={popoverActions}
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, width: 640, maxWidth: 'calc(100vw - 48px)', maxHeight: 'calc(100vh - 48px)' } }}
      >
        <Stack
          onTransitionEnd={() => popoverActions.current?.updatePosition()}
          spacing={1.5}
          role="dialog"
          aria-label={
            kind === 'normalization'
              ? 'Panel channel normalization'
              : kind === 'rna'
              ? 'Set panel RNA ranges'
              : 'Edit selected panel ranges'
          }
        >
          <Typography variant="subtitle1">
            {kind === 'normalization'
              ? 'Channel normalization'
              : kind === 'rna'
              ? 'Set RNA ranges'
              : 'Edit selected ranges'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {panel.name || panel.panelId.replace('panel-', 'Panel ')} · {panel.slides.length} member slides
          </Typography>
          {kind !== 'bulk' && (
            <TextField
              select
              label="Apply to"
              value={scope}
              onChange={(event) => {
                setScope(event.target.value as MarkerScope);
                setAcknowledgedScope(null);
              }}
            >
              {kind === 'normalization' && <MenuItem value="missing">mIF missing normalization</MenuItem>}
              <MenuItem value="all">{kind === 'rna' ? 'All RNA probes' : 'All mIF markers'}</MenuItem>
              <MenuItem value="selected">
                {kind === 'rna' ? 'Selected RNA probes' : 'Explicitly selected channels'}
              </MenuItem>
            </TextField>
          )}
          {kind === 'normalization' ? (
            <>
              <TextField
                select
                label="Method"
                value={method}
                onChange={(event) => {
                  setMethod(event.target.value as NormalizationMethod);
                  setAcknowledgedScope(null);
                }}
              >
                <MenuItem value={NormalizationMethod.Auto}>Automatic (default)</MenuItem>
                <MenuItem value={NormalizationMethod.Parametrized}>Manual parameters</MenuItem>
              </TextField>
              {method === NormalizationMethod.Auto ? (
                <TextField
                  label="Normalization model"
                  value={selectedModelUrl || ''}
                  onChange={(event) => {
                    setModelOverride(event.target.value);
                    setAcknowledgedScope(null);
                  }}
                  helperText={modelOverride === null ? 'Default model' : 'Custom model'}
                />
              ) : (
                <NormalizationParamsForm normalizationConfig={parameters} setNormalizationConfig={setParameters} />
              )}
              <TextField
                select
                label="Slides"
                value={slideScope}
                onChange={(event) => {
                  setSlideScope(event.target.value as 'all' | 'selected');
                  setAcknowledgedScope(null);
                }}
              >
                <MenuItem value="all">All member slides</MenuItem>
                <MenuItem value="selected">Selected member slides</MenuItem>
              </TextField>
              {slideScope === 'selected' && (
                <>
                  <Stack direction="row" spacing={1}>
                    <Button
                      color="inherit"
                      onClick={() => {
                        setSelectedSlideIds(memberSlideIds);
                        setAcknowledgedScope(null);
                      }}
                    >
                      Select all slides
                    </Button>
                    <Button
                      color="inherit"
                      onClick={() => {
                        setSelectedSlideIds([]);
                        setAcknowledgedScope(null);
                      }}
                    >
                      Clear slide selection
                    </Button>
                  </Stack>
                  <PanelSlideSummary
                    panel={panel}
                    labId={casesParams.labId}
                    selectedSlideIds={selectedSlideIds}
                    onSelectionChange={(slideId, checked) => {
                      setSelectedSlideIds((current) =>
                        checked ? uniq([...current, slideId]) : filter(current, (entry) => entry !== slideId)
                      );
                      setAcknowledgedScope(null);
                    }}
                    disabled={mutation.isLoading}
                  />
                </>
              )}
              {warningMessages.length > 0 && (
                <Alert severity="warning">
                  <Stack spacing={1}>
                    {map(warningMessages, (message) => (
                      <Typography key={message} variant="body2">
                        {message}
                      </Typography>
                    ))}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={acknowledgedScope === scopeSignature}
                          onChange={(_event, checked) => setAcknowledgedScope(checked ? scopeSignature : null)}
                        />
                      }
                      label="I understand these warnings and want to continue"
                    />
                  </Stack>
                </Alert>
              )}
              {method === NormalizationMethod.Auto && !hasKnownAutomaticHistory(targetMarkerIds, targetSlideIds) && (
                <Alert severity="info">
                  Automatic-normalization history is unavailable. Reload panel information before running.
                </Alert>
              )}
              {!targetSlideIds.length && (
                <Typography variant="body2" color="text.secondary">
                  Select at least one member slide to run normalization.
                </Typography>
              )}
              {targetSlideIds.length > 0 && !hasAllMarkers(targetMarkerIds, targetSlideIds) && (
                <Alert severity="info">
                  Some chosen slides do not contain every selected marker. Adjust the marker or slide selection.
                </Alert>
              )}
              {some(targetMarkerIds, (markerId) => includes(pendingMarkerIds, markerId)) && (
                <Alert severity="info">
                  Save or discard selected-channel range edits before running normalization.
                </Alert>
              )}
            </>
          ) : (
            <>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Minimum"
                  required
                  type="number"
                  value={minimum}
                  onChange={(event) => setMinimum(event.target.value)}
                  inputProps={{ min: 0, max: maximumValue, step: 1 }}
                  error={minimum !== '' && !Number.isInteger(Number(minimum))}
                />
                <TextField
                  label="Maximum"
                  required
                  type="number"
                  value={maximum}
                  onChange={(event) => setMaximum(event.target.value)}
                  inputProps={{ min: 0, max: maximumValue, step: 1 }}
                  error={maximum !== '' && !Number.isInteger(Number(maximum))}
                />
              </Stack>
              <Typography variant="caption" color={rangesHaveFractionalValues ? 'error' : 'text.secondary'}>
                {rangesHaveFractionalValues
                  ? 'Enter whole-number values.'
                  : maximumValue === undefined
                  ? 'Verified bit-depth metadata is required for every chosen marker.'
                  : `Enter values from 0 to ${maximumValue}. Current results remain unchanged until Save range changes.`}
              </Typography>
            </>
          )}
          <Typography variant="body2">
            {targetMarkerIds.length}{' '}
            {kind === 'rna'
              ? targetMarkerIds.length === 1
                ? 'RNA probe'
                : 'RNA probes'
              : targetMarkerIds.length === 1
              ? 'channel'
              : 'channels'}{' '}
            · {kind === 'normalization' ? targetSlideIds.length : panel.slides.length}{' '}
            {(kind === 'normalization' ? targetSlideIds.length : panel.slides.length) === 1 ? 'slide' : 'slides'}
          </Typography>
          {mutation.isError && kind === 'normalization' && (
            <Alert ref={failureAlert} severity="error">
              Normalization could not be started. Your selection and settings are retained; check jobs before retrying.
            </Alert>
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button color="inherit" disabled={mutation.isLoading} onClick={close}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              aria-busy={kind === 'normalization' && mutation.isLoading}
              startIcon={
                kind === 'normalization' && mutation.isLoading ? (
                  <CircularProgress size={18} color="inherit" aria-label="Starting normalization" />
                ) : undefined
              }
              disabled={kind === 'normalization' ? !runValid : !targetMarkerIds.length || !rangesValid}
              onClick={async () => {
                if (kind === 'normalization') {
                  if (runValid && (await run(targetMarkerIds, targetSlideIds, method, selectedModelUrl))) {
                    setConfirmedSettings({ scope, method, modelOverride, parameters, slideScope, selectedSlideIds });
                  }
                } else if (rangesValid) {
                  onStageRanges(targetMarkerIds, Number(minimum), Number(maximum));
                  close();
                }
              }}
            >
              {kind === 'normalization'
                ? mutation.isLoading
                  ? 'Starting normalization...'
                  : 'Run normalization'
                : 'Use ranges'}
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </Stack>
  );
};
